/**
 * Structured logging seam. The core logs English messages with structured fields; sinks decide the format.
 *
 * Secret redaction is central: every sink below passes its fields through `redactFields`, so tokens, passwords,
 * authorization headers, secrets, request payloads and game states never reach a log line, whatever a call site
 * passes. Errors are reduced to their message and stack (plus a system error code such as EADDRINUSE).
 */
export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export type LogFields = Record<string, unknown>;

export interface Logger {
  debug(msg: string, fields?: LogFields): void;
  info(msg: string, fields?: LogFields): void;
  warn(msg: string, fields?: LogFields): void;
  error(msg: string, fields?: LogFields): void;
  child(fields: LogFields): Logger;
}

export const LOG_LEVELS: readonly LogLevel[] = ['debug', 'info', 'warn', 'error'];

const LEVEL_ORDER: Record<LogLevel, number> = { debug: 10, info: 20, warn: 30, error: 40 };

export const noopLogger: Logger = {
  debug() {},
  info() {},
  warn() {},
  error() {},
  child() {
    return noopLogger;
  },
};

export const REDACTED = '[redacted]';

/**
 * Field names whose values are never logged: credentials of every kind (case-insensitive substring match: token,
 * deviceToken, sessionToken, tokenHash, adminToken, password, passwordHash, secret (also proxySecret and the header
 * x-fwp-proxy-secret), authorization, cookie, apiKey),
 * game states, and whole request payloads / bodies / legacy saves (exact names).
 */
const SECRET_KEY = /token|passw|secret|authoriz|cookie|api_?key|gamestate|serverstate/i;
const BULK_KEYS = new Set(['payload', 'body', 'legacy', 'legacyreplay', 'raw']);

/**
 * Secrets embedded in free text: bearer tokens, JSON members with a secret-looking name, and the proxy secret header
 * (X-FWP-Proxy-Secret: <PROXY_SECRET>, e.g. in a dumped request).
 */
const BEARER = /\b(Bearer)\s+[A-Za-z0-9._~+/=-]+/gi;
const PROXY_SECRET_TEXT = /(x-fwp-proxy-secret['"]?\s*[:=]\s*['"]?)[^\s'",;}]+/gi;
const JSON_SECRET = /("[A-Za-z_]*(?:token|password|secret|authorization)[A-Za-z_]*"\s*:\s*)"(?:[^"\\]|\\.)*"/gi;

export function isSecretKey(key: string): boolean {
  return SECRET_KEY.test(key) || BULK_KEYS.has(key.toLowerCase());
}

function scrubText(s: string): string {
  if (s.length > 8192) s = `${s.slice(0, 8192)}…[${s.length - 8192} more chars]`;
  return s.replace(BEARER, `$1 ${REDACTED}`).replace(JSON_SECRET, `$1"${REDACTED}"`).replace(PROXY_SECRET_TEXT, `$1${REDACTED}`);
}

function errorValue(err: Error): Record<string, unknown> {
  const out: Record<string, unknown> = { message: scrubText(String(err.message ?? '')) };
  if (err.stack) out.stack = scrubText(err.stack);
  const code = (err as { code?: unknown }).code;
  if (typeof code === 'string' && /^[A-Z0-9_]{1,40}$/.test(code)) out.code = code;
  return out;
}

function redactValue(v: unknown, depth: number, seen: WeakSet<object>): unknown {
  if (typeof v === 'string') return scrubText(v);
  if (v === null || typeof v !== 'object') return typeof v === 'bigint' || typeof v === 'function' || typeof v === 'symbol' ? String(v) : v;
  if (v instanceof Error) return errorValue(v);
  if (v instanceof Date) return v.toISOString();
  if (ArrayBuffer.isView(v) || v instanceof ArrayBuffer) return `[binary ${(v as ArrayBuffer).byteLength} bytes]`;
  if (seen.has(v)) return '[circular]';
  if (depth >= 8) return '[truncated]';
  seen.add(v);
  try {
    if (Array.isArray(v)) return v.slice(0, 200).map((x) => redactValue(x, depth + 1, seen));
    if (v instanceof Map) return `[Map ${v.size}]`;
    if (v instanceof Set) return `[Set ${v.size}]`;
    const out: Record<string, unknown> = {};
    for (const [k, x] of Object.entries(v)) out[k] = isSecretKey(k) ? (x === undefined || x === null ? x : REDACTED) : redactValue(x, depth + 1, seen);
    return out;
  } finally {
    seen.delete(v);
  }
}

/** Copy of `fields` that is safe to log (see the module comment). */
export function redactFields(fields: LogFields): LogFields {
  return redactValue(fields, 0, new WeakSet()) as LogFields;
}

/** JSON-lines logger: one object per line `{ts, level, msg, ...fields}`. */
export function createJsonLogger(opts: { level?: LogLevel; write?: (line: string) => void; now?: () => number; base?: LogFields } = {}): Logger {
  const min = LEVEL_ORDER[opts.level ?? 'info'];
  const write = opts.write ?? ((line: string) => console.log(line));
  const now = opts.now ?? (() => Date.now());
  const make = (base: LogFields): Logger => {
    const log = (level: LogLevel, msg: string, fields?: LogFields) => {
      if (LEVEL_ORDER[level] < min) return;
      const rec = { ts: new Date(now()).toISOString(), level, msg, ...base, ...(fields ? redactFields(fields) : {}) };
      let line: string;
      try {
        line = JSON.stringify(rec);
      } catch {
        line = JSON.stringify({ ts: rec.ts, level, msg, note: 'unserializable fields' });
      }
      write(line);
    };
    return {
      debug: (m, f) => log('debug', m, f),
      info: (m, f) => log('info', m, f),
      warn: (m, f) => log('warn', m, f),
      error: (m, f) => log('error', m, f),
      child: (f) => make({ ...base, ...redactFields(f) }),
    };
  };
  return make(redactFields(opts.base ?? {}));
}

/** Logger that keeps (redacted) records in memory (tests). */
export function createMemoryLogger(): Logger & { records: ({ level: LogLevel; msg: string } & LogFields)[] } {
  const records: ({ level: LogLevel; msg: string } & LogFields)[] = [];
  const make = (base: LogFields): Logger => {
    const push = (level: LogLevel, msg: string, f?: LogFields) => void records.push({ level, msg, ...base, ...(f ? redactFields(f) : {}) });
    return {
      debug: (msg, f) => push('debug', msg, f),
      info: (msg, f) => push('info', msg, f),
      warn: (msg, f) => push('warn', msg, f),
      error: (msg, f) => push('error', msg, f),
      child: (f) => make({ ...base, ...redactFields(f) }),
    };
  };
  return Object.assign(make({}), { records });
}
